import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button
} from '@repo/ui'
import { failureLine } from '@/common/errorMessages'
import type { ReactNode } from 'react'

export function CanvasConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  destructive = false,
  onConfirm,
  onOpenChange
}: {
  open: boolean
  title: string
  body: string
  confirmLabel: string
  destructive?: boolean
  onConfirm: () => Promise<void>
  onOpenChange: (open: boolean) => void
}): ReactNode {
  // The body owns the pending/error state and mounts fresh per open cycle, so reopening after a
  // failure never inherits the previous attempt's error.
  if (!open) return null
  return (
    <ConfirmDialogBody
      title={title}
      body={body}
      confirmLabel={confirmLabel}
      destructive={destructive}
      onConfirm={onConfirm}
      onOpenChange={onOpenChange}
    />
  )
}

function ConfirmDialogBody({
  title,
  body,
  confirmLabel,
  destructive,
  onConfirm,
  onOpenChange
}: {
  title: string
  body: string
  confirmLabel: string
  destructive: boolean
  onConfirm: () => Promise<void>
  onOpenChange: (open: boolean) => void
}): ReactNode {
  const [confirming, setConfirming] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  function requestClose(next: boolean) {
    // A dismissal while the confirmation is still running would abandon it mid-flight.
    if (!next && confirming) return
    if (!next) setFailure(null)
    onOpenChange(next)
  }

  async function confirm() {
    setConfirming(true)
    setFailure(null)
    try {
      await onConfirm()
      onOpenChange(false)
    } catch (error) {
      setFailure(failureLine(error, 'The action did not complete.'))
    } finally {
      setConfirming(false)
    }
  }

  return (
    <AlertDialog open onOpenChange={requestClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{body}</AlertDialogDescription>
        </AlertDialogHeader>
        {failure !== null && (
          <p role="alert" className="text-sm text-destructive">
            {failure}
          </p>
        )}
        <AlertDialogFooter>
          <Button type="button" variant="outline" disabled={confirming} onClick={() => requestClose(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant={destructive ? 'destructive' : 'default'}
            disabled={confirming}
            onClick={() => void confirm()}
          >
            {confirming && <Loader2 className="size-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
            {confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
